import React from 'react'
import {useIntl} from 'react-intl'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'

import {LocationAttractions} from './LocationAttractions'

import type {LocationContentVariant} from './types'
import type {ContentHotel} from '@findhotel/sapi'

interface HotelLocationContentProps {
  descriptions?: ContentHotel['descriptions']
  attractions?: ContentHotel['location']['poi']
  variant?: LocationContentVariant
}

export const HotelLocationContent = ({
  descriptions,
  attractions,
  variant = 'overlay'
}: HotelLocationContentProps) => {
  const {formatMessage} = useIntl()
  const {isMobile} = useDeviceLayout()
  const {location} = descriptions || {}
  const hasLocation = location && Object.keys(location).length > 0
  const hasAttractions = attractions?.length > 0

  if (!hasAttractions && !hasLocation) return null

  switch (variant) {
    case 'ap':
      return (
        <>
          {hasLocation && <Text dir="auto">{descriptions.location}</Text>}
          {hasAttractions && hasLocation && <Box paddingTop="s600" />}
          <LocationAttractions attractions={attractions} variant={variant} />
        </>
      )
    case 'apCombined':
    case 'apMobile':
      return <LocationAttractions attractions={attractions} variant={variant} />
    case 'amenitiesTab':
      return (
        <>
          <Box
            display="flex"
            gap="s300"
            alignItems="center"
            marginBottom="s500"
          >
            <Icon size="md" colorPath="content.neutral.primary" name="Pin" />
            <Text variant="titleL" tag="p">
              {formatMessage({
                id: 'location',
                defaultMessage: 'Location'
              })}
            </Text>
          </Box>
          {hasLocation && <Text dir="auto">{descriptions.location}</Text>}
          {hasAttractions && (
            <>
              <Box
                paddingTop={hasLocation ? 's700' : undefined}
                paddingBottom="s300"
              >
                <Text variant="labelM" colorPath="content.neutral.primary">
                  {formatMessage({
                    id: 'location.whatsNearby',
                    defaultMessage: "What's nearby"
                  })}
                </Text>
              </Box>
              <LocationAttractions
                attractions={attractions}
                variant={variant}
              />
            </>
          )}
        </>
      )
    case 'detailsTab':
      return (
        <>
          <Box
            display="flex"
            gap="s300"
            alignItems="center"
            marginBottom="s500"
          >
            <Text variant="labelM">
              {formatMessage({
                id: 'location',
                defaultMessage: 'Location'
              })}
            </Text>
          </Box>
          {hasLocation && <Text dir="auto">{descriptions.location}</Text>}
          {hasAttractions && (
            <>
              <Box
                paddingTop={hasLocation ? 's700' : undefined}
                paddingBottom="s300"
              >
                <Text variant="labelM" colorPath="content.neutral.primary">
                  {formatMessage({
                    id: 'location.whatsNearby',
                    defaultMessage: "What's nearby"
                  })}
                </Text>
              </Box>
              <LocationAttractions
                attractions={attractions}
                variant={variant}
              />
            </>
          )}
        </>
      )
    case 'bottomSheet':
      return (
        <>
          {hasLocation && (
            <>
              <Box
                display="flex"
                gap="s300"
                alignItems="center"
                marginBottom="s500"
              >
                <Icon
                  size="md"
                  colorPath="content.neutral.primary"
                  name="Trees"
                />
                <Text variant={isMobile ? 'titleL' : 'titleM'} tag="p">
                  {formatMessage({
                    id: 'location.aboutArea',
                    defaultMessage: 'About the area'
                  })}
                </Text>
              </Box>
              <Text dir="auto">{descriptions.location}</Text>
            </>
          )}
          {hasAttractions && hasLocation && (
            <Box paddingBottom="s700" paddingTop="s700">
              <Divider />
            </Box>
          )}
          {hasAttractions && (
            <>
              <Box
                display="flex"
                gap="s300"
                alignItems="center"
                marginBottom="s500"
              >
                <Icon
                  size="md"
                  colorPath="content.neutral.primary"
                  name="Pin"
                />
                <Text variant={isMobile ? 'titleL' : 'titleM'} tag="p">
                  {formatMessage({
                    id: 'location.whatsNearby',
                    defaultMessage: "What's nearby"
                  })}
                </Text>
              </Box>
              <LocationAttractions
                attractions={attractions}
                variant={variant}
              />
            </>
          )}
        </>
      )
    case 'overlay':
    default:
      return (
        <>
          {hasLocation && (
            <>
              <Box
                display="flex"
                gap="s300"
                alignItems="center"
                marginBottom="s500"
              >
                <Icon
                  size="md"
                  colorPath="content.neutral.primary"
                  name="Trees"
                />
                <Text variant={isMobile ? 'titleL' : 'titleM'} tag="p">
                  {formatMessage({
                    id: 'location.aboutArea',
                    defaultMessage: 'About the area'
                  })}
                </Text>
              </Box>
              <Text dir="auto">{descriptions.location}</Text>
            </>
          )}
          {hasAttractions && hasLocation && (
            <Box paddingBottom="s600" paddingTop="s600">
              <Divider />
            </Box>
          )}
          {hasAttractions && (
            <>
              <Box
                display="flex"
                gap="s300"
                alignItems="center"
                marginBottom="s500"
              >
                <Icon
                  size="md"
                  colorPath="content.neutral.primary"
                  name="Pin"
                />
                <Text variant={isMobile ? 'titleL' : 'titleM'} tag="p">
                  {formatMessage({
                    id: 'location.whatsNearby',
                    defaultMessage: "What's nearby"
                  })}
                </Text>
              </Box>
              <LocationAttractions
                attractions={attractions}
                variant={variant}
              />
            </>
          )}
        </>
      )
  }
}
